import type { ReactNode } from "react";
import { allDocPages } from "./content";
import DocsLayout from "./layout";

const markdown = `
CloudQuote supports several authentication mechanisms, each tuned for a different deployment shape. The right one for you depends on **where your code runs** and **what you can keep secret**.

## How CloudQuote thinks about auth

The platform splits applications into two categories:

- **Front-end applications** — code runs on a user's device (browser, mobile, desktop). Anything embedded in the binary or page source is assumed to be public.
- **Back-end applications** — code runs on a server you control. Secrets stored on the server are assumed to remain private.

Each category has its own set of supported credential types.

## Credential types at a glance

| Credential | Used by | Secret? | Common pairing |
| :---- | :---- | :---- | :---- |
| Whitelisted domain | Front-end | No | CORS \`Origin\` / \`Referer\` checks |
| Public application key | Front-end with auth | No | User key, OAuth, or whitelisted IP range |
| Private application key | Back-end | Yes — treat like a password | None required |
| Whitelisted IP range | Back-end (enterprise) | No | Public application key |
| User key (OAuth, JWT, etc.) | Front-end | Short-lived | Public application key |

## Front-end authentication

### Whitelisted domain (most common)

Once you whitelist a domain in the API Dashboard, requests from that origin are accepted with **no key in the request**. CloudQuote enforces:

- The request includes valid CORS headers.
- Both \`Referer:\` and \`Origin:\` match the whitelisted domain (or one of its subdomains).
- The request does **not** include any private application keys.
- The traffic pattern doesn't match known abuse heuristics.

Subdomains of a whitelisted domain are accepted automatically.

### Public application key + secondary auth

A public application key identifies *the application* but does not authorize a specific user — it must be paired with one of:

- A **user key** (OAuth token, JWT, or any short-lived credential)
- A **whitelisted IP range** (typical for enterprise deployments)

Public keys can be embedded in client code; they are not secrets.

## Back-end authentication

### Private application key (most common)

Pass the key with each request. The request **must not** carry CORS headers, a \`Referer:\`, or an \`Origin:\` — these are the markers of a browser request, and the platform will reject the call to prevent key leakage.

Treat a private key like a password. If a private key is ever exposed in client code, rotate it immediately.

### Public key + whitelisted IP range

For enterprise back-ends where the same key is shared by many servers, use a public key plus an IP whitelist. CloudQuote rejects the request unless the source IP falls inside the registered range.

## Working with user keys

A user key is whatever your app uses to authorize an end user — most often an **OAuth access token** or a **JWT**.

When CloudQuote sees a user key for the first time, it calls back to a verification endpoint **you define**. After that, it re-verifies on a configurable interval. This lets you stay the source of truth for who can use CloudQuote on behalf of your application.

> **Important:** user keys must be short-lived (1–5 minutes is recommended). Long-lived user keys make request spoofing much easier.

## OAuth

CloudQuote supports OAuth of any version, but the OAuth flow itself lives in your application. CloudQuote's role is passive: once you have an access token, pass it as a user key and CloudQuote will verify it via your endpoint.

## Creating credentials

1. Sign in to the [API Dashboard](/login).
2. Open **My Account → API Credentials**.
3. Choose the credential type that matches your deployment.

Each credential has its own set of fields — domains for whitelists, IP ranges for IP whitelists, and so on.

## Quick decision guide

- **Pure server script?** → Private key.
- **Static website calling CloudQuote from JS?** → Whitelisted domain.
- **Single-page app with login?** → Whitelisted domain *or* public key + user key.
- **Internal enterprise back-end?** → Public key + whitelisted IP range.
`;

export default function AuthenticationPage(): ReactNode {
  const page = allDocPages.find(p => p.slug === "authentication")!;
  return <DocsLayout page={page} markdown={markdown}/>;
}
